import { validateTouch, MAX_AGE_SECONDS } from "./attributionTouch.js";
import { MARKETING_PAGES, PAGE_CATALOGUE_VERSION } from "./attributionPages.js";

export const MAX_OBSERVATIONS = 50;
export const MAX_ATTRIBUTION_BYTES = 32 * 1024;
export const JOURNEY_KEY = "ap_acquisition_journey_v2";
export const GENERATION_COOKIE = "__Secure-ap_acquisition_generation_v2";
export const GENERATION_KEY = "ap_acquisition_generation_v2";
const REASONS = ["legacy", "summary_only", "limit", "handoff_limit", "expired", "invalid", "storage_unavailable"];
const ID = /^[0-9a-z]{1,10}\.[0-9a-f]{16}$/;
const generationValid = value => typeof value === "string" && /^[0-9a-f]{32}$/.test(value);
export const bytes = value => new TextEncoder().encode(JSON.stringify(value)).length;
export const nonDirect = touch => touch &&
  !(touch.source === "direct" && touch.medium === "none") &&
  !(touch.source === "unknown" && touch.medium === "unknown");
export const idOrder = (a, b) => parseInt(a.split(".")[0], 36) - parseInt(b.split(".")[0], 36) || a.localeCompare(b);

export function randomHex(length = 16) {
  return [...globalThis.crypto.getRandomValues(new Uint8Array(length))].map(n => n.toString(16).padStart(2, "0")).join("");
}

export function nextObservationId(record) {
  const ids = record.arrivals.flatMap(a => [a.id, ...a.pages.map(p => p[0])]);
  if (record.last_observation) ids.push(record.last_observation);
  const sequence = Math.max(0, ...ids.map(id => parseInt(id.split(".")[0], 36))) + 1;
  return sequence.toString(36) + "." + randomHex(8);
}

export function markIncomplete(record, reason) {
  if (!record.completeness.reasons.includes(reason)) record.completeness.reasons.push(reason);
  record.completeness.reasons.sort();
  record.completeness.partial = true;
}

export function observationCount(record) {
  return record.arrivals.reduce((count, a) => count + 1 + a.pages.length, 0);
}

// Remove lower-value observations first; the original summary is never evicted.
export function dropObservation(record) {
  const withPages = record.arrivals.filter(a => a.pages.length)
    .sort((a, b) => idOrder(a.pages[0][0], b.pages[0][0]))[0];
  if (withPages) { withPages.pages.shift(); return true; }
  const direct = record.arrivals.findIndex(a => !nonDirect(a.touch));
  if (!record.arrivals.length) return false;
  const [removed] = record.arrivals.splice(direct < 0 ? 0 : direct, 1);
  if (record.active_arrival === removed.id) record.active_arrival = null;
  return true;
}

export function boundJourney(record, fits = r => observationCount(r) <= MAX_OBSERVATIONS && bytes(r) <= MAX_ATTRIBUTION_BYTES, reason = "limit") {
  while (!fits(record)) {
    markIncomplete(record, reason);
    if (!dropObservation(record)) break;
  }
  return fits(record) ? record : null;
}

export function emptyJourney(generation) {
  return { version: 2, catalogue_version: PAGE_CATALOGUE_VERSION, generation,
    first_touch: null, signup_touch: null, active_arrival: null, last_observation: null, arrivals: [],
    completeness: { partial: false, reasons: [] } };
}

export function validateGeneration(value, now = Date.now()) {
  return value && generationValid(value.id) && typeof value.closed === "boolean" &&
    Number.isSafeInteger(value.expires_at) && value.expires_at > now && value.expires_at <= now + MAX_AGE_SECONDS * 1000 + 300000
    ? { id: value.id, closed: value.closed, expires_at: value.expires_at } : null;
}

export function validateJourney(candidate, now = Date.now()) {
  if (!candidate || candidate.version !== 2 || candidate.catalogue_version !== PAGE_CATALOGUE_VERSION ||
      !generationValid(candidate.generation) || !Array.isArray(candidate.arrivals) || bytes(candidate) > 256 * 1024) return null;
  const result = emptyJourney(candidate.generation);
  result.first_touch = validateTouch(candidate.first_touch, now);
  result.signup_touch = validateTouch(candidate.signup_touch, now);
  for (const reason of candidate.completeness?.reasons || []) if (REASONS.includes(reason)) markIncomplete(result, reason);
  const seen = new Set();
  for (const a of candidate.arrivals.slice(0, 1000)) {
    if (!a || !ID.test(a.id) || seen.has(a.id) || !["m", "i"].includes(a.site) || !Array.isArray(a.pages)) {
      markIncomplete(result, "invalid"); continue;
    }
    const touch = validateTouch(a.site === "m" ? { ...a.touch, site: "m" } : a.touch, now);
    if (!touch) { markIncomplete(result, "expired"); continue; }
    seen.add(a.id);
    const pages = [];
    for (const page of a.pages.slice(0, 1000)) {
      if (!Array.isArray(page) || !ID.test(page[0]) || seen.has(page[0]) || !Object.hasOwn(MARKETING_PAGES, page[1])) {
        markIncomplete(result, "invalid"); continue;
      }
      seen.add(page[0]);
      pages.push([page[0], page[1]]);
    }
    pages.sort((a, b) => idOrder(a[0], b[0]));
    result.arrivals.push({ id: a.id, site: a.site, touch, pages });
  }
  result.arrivals.sort((a, b) => Date.parse(a.touch.captured_at) - Date.parse(b.touch.captured_at) || idOrder(a.id, b.id));
  result.first_touch ||= result.arrivals[0]?.touch || null;
  const meaningful = result.arrivals.filter(a => nonDirect(a.touch));
  result.signup_touch ||= meaningful.at(-1)?.touch || result.arrivals.at(-1)?.touch || null;
  result.active_arrival = result.arrivals.some(a => a.id === candidate.active_arrival) ? candidate.active_arrival : null;
  result.last_observation = ID.test(candidate.last_observation) ? candidate.last_observation : null;
  if (!result.first_touch && !result.signup_touch && !result.arrivals.length) return null;
  return boundJourney(result);
}

export function upgradeJourney(record, generation) {
  if (record?.version === 2) return record;
  const result = emptyJourney(generation);
  if (record) {
    result.first_touch = record.first_touch;
    result.signup_touch = record.signup_touch;
    markIncomplete(result, "legacy");
  }
  return result;
}

export function mergeJourneys(records, generation, now = Date.now()) {
  const result = emptyJourney(generation);
  const arrivals = new Map();
  const firsts = [], latest = [];
  for (const raw of records.filter(Boolean)) {
    const record = upgradeJourney(raw, generation);
    if (record.generation !== generation) continue;
    if (record.first_touch) firsts.push(record.first_touch);
    if (record.signup_touch) latest.push(record.signup_touch);
    for (const reason of record.completeness.reasons) markIncomplete(result, reason);
    for (const a of record.arrivals) {
      const previous = arrivals.get(a.id);
      const pages = new Map([...(previous?.pages || []), ...a.pages].map(p => [p[0], p]));
      arrivals.set(a.id, { ...a, pages: [...pages.values()] });
    }
    if (record.active_arrival) result.active_arrival = record.active_arrival;
    if (record.last_observation && (!result.last_observation || idOrder(record.last_observation, result.last_observation) > 0)) result.last_observation = record.last_observation;
  }
  result.first_touch = firsts.sort((a, b) => Date.parse(a.captured_at) - Date.parse(b.captured_at))[0] || null;
  const meaningful = latest.filter(nonDirect);
  result.signup_touch = (meaningful.length ? meaningful : latest).sort((a, b) => Date.parse(b.captured_at) - Date.parse(a.captured_at))[0] || null;
  result.arrivals = [...arrivals.values()];
  return validateJourney(result, now);
}
